import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../utils/axios';

interface User {
  id: number;
  email: string;
  full_name: string;
  role: string;
  credits: number;
  email_verified: boolean;
  created_at: string;
  total_projects: number;
  total_credits_used: number;
  last_activity: string | null;
}

const AdminUsers: React.FC = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreditsModal, setShowCreditsModal] = useState(false);
  const [editForm, setEditForm] = useState({ full_name: '', role: '', email_verified: false });
  const [creditsForm, setCreditsForm] = useState({ amount: 0, reason: '' });

  useEffect(() => {
    fetchUsers();
  }, [roleFilter]);

  const fetchUsers = async () => {
    try {
      const params: any = {};
      if (roleFilter) params.role = roleFilter;
      if (searchTerm) params.search = searchTerm;

      const response = await axios.get('/api/admin/users', { params });
      setUsers(response.data);
      setLoading(false);
    } catch (err: any) {
      if (err.response?.status === 403) {
        setError('Access denied. Admin privileges required.');
      } else {
        setError('Failed to load users');
      }
      setLoading(false);
    }
  };

  const handleSearch = () => {
    fetchUsers();
  };

  const handleEditUser = (user: User) => {
    setSelectedUser(user);
    setEditForm({
      full_name: user.full_name,
      role: user.role,
      email_verified: user.email_verified
    });
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    if (!selectedUser) return;

    try {
      await axios.put(`/api/admin/users/${selectedUser.id}`, editForm);
      setShowEditModal(false);
      fetchUsers();
    } catch (err) {
      alert('Failed to update user');
    }
  };

  const handleAdjustCredits = (user: User) => {
    setSelectedUser(user);
    setCreditsForm({ amount: 0, reason: '' });
    setShowCreditsModal(true);
  };

  const handleSaveCredits = async () => {
    if (!selectedUser) return;

    try {
      await axios.post(`/api/admin/users/${selectedUser.id}/credits`, creditsForm);
      setShowCreditsModal(false);
      fetchUsers();
    } catch (err) {
      alert('Failed to adjust credits');
    }
  };

  const getRoleBadge = (role: string) => {
    const badges: any = {
      admin: 'badge-error',
      senior_qa: 'badge-warning',
      qa_reviewer: 'badge-primary',
      user: 'badge-secondary'
    };
    return badges[role] || 'badge-secondary';
  };

  if (loading) {
    return (
      <div className="loading-page">
        <p>Loading users...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page">
        <div className="container" style={{ paddingTop: '2rem' }}>
          <div className="alert alert-error">
            <span className="alert-icon">⚠️</span>
            <span>{error}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="page-header">
        <div className="container">
          <div className="page-header-content">
            <div className="page-header-left">
              <button onClick={() => navigate('/admin')} className="btn btn-sm btn-ghost">
                ← Back
              </button>
              <h1 className="page-title">👥 User Management</h1>
            </div>
          </div>
        </div>
      </div>

      <div className="page-content">
        <div className="container">
          {/* Search and Filters */}
          <div className="card" style={{ marginBottom: 'var(--spacing-6)' }}>
            <div className="card-body">
              <div style={{ display: 'flex', gap: 'var(--spacing-4)', flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: '250px' }}>
                  <input
                    type="text"
                    className="input"
                    placeholder="Search by email or name..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  />
                </div>
                <select
                  className="input"
                  style={{ width: '200px' }}
                  value={roleFilter}
                  onChange={(e) => setRoleFilter(e.target.value)}
                >
                  <option value="">All Roles</option>
                  <option value="user">User</option>
                  <option value="qa_reviewer">QA Reviewer</option>
                  <option value="senior_qa">Senior QA</option>
                  <option value="admin">Admin</option>
                </select>
                <button onClick={handleSearch} className="btn btn-primary">
                  Search
                </button>
              </div>
            </div>
          </div>

          {/* Users Table */}
          <div className="card">
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Role</th>
                    <th>Credits</th>
                    <th>Projects</th>
                    <th>Status</th>
                    <th>Joined</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.length === 0 ? (
                    <tr>
                      <td colSpan={7}>
                        <div className="empty-state">
                          <div className="empty-state-title">No users found</div>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    users.map((user) => (
                      <tr key={user.id}>
                        <td>
                          <div>
                            <div style={{ fontWeight: 'var(--font-semibold)', color: 'var(--color-gray-900)' }}>
                              {user.full_name || 'N/A'}
                            </div>
                            <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-gray-500)' }}>
                              {user.email}
                            </div>
                          </div>
                        </td>
                        <td>
                          <span className={`badge ${getRoleBadge(user.role)}`}>
                            {user.role}
                          </span>
                        </td>
                        <td>
                          <strong>{user.credits}</strong>
                          <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-gray-500)' }}>
                            {user.total_credits_used} used
                          </div>
                        </td>
                        <td>{user.total_projects}</td>
                        <td>
                          {user.email_verified ? (
                            <span className="badge badge-success">✓ Verified</span>
                          ) : (
                            <span className="badge badge-warning">Pending</span>
                          )}
                        </td>
                        <td style={{ fontSize: 'var(--text-xs)', color: 'var(--color-gray-500)' }}>
                          {new Date(user.created_at).toLocaleDateString()}
                        </td>
                        <td>
                          <div style={{ display: 'flex', gap: 'var(--spacing-2)' }}>
                            <button
                              onClick={() => handleEditUser(user)}
                              className="btn btn-sm btn-outline"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleAdjustCredits(user)}
                              className="btn btn-sm btn-ghost"
                            >
                              Credits
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Edit User Modal */}
      {showEditModal && selectedUser && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Edit User</h3>
              <button className="modal-close" onClick={() => setShowEditModal(false)}>
                ✕
              </button>
            </div>
            <div className="modal-body">
              <div className="input-group">
                <label className="input-label">Full Name</label>
                <input
                  type="text"
                  className="input"
                  value={editForm.full_name}
                  onChange={(e) => setEditForm({ ...editForm, full_name: e.target.value })}
                />
              </div>
              <div className="input-group">
                <label className="input-label">Role</label>
                <select
                  className="input"
                  value={editForm.role}
                  onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
                >
                  <option value="user">User</option>
                  <option value="qa_reviewer">QA Reviewer</option>
                  <option value="senior_qa">Senior QA</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <div className="input-group">
                <label className="input-label" style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
                  <input
                    type="checkbox"
                    checked={editForm.email_verified}
                    onChange={(e) => setEditForm({ ...editForm, email_verified: e.target.checked })}
                  />
                  Email Verified
                </label>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setShowEditModal(false)}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={handleSaveEdit}>
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Adjust Credits Modal */}
      {showCreditsModal && selectedUser && (
        <div className="modal-overlay" onClick={() => setShowCreditsModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Adjust Credits</h3>
              <button className="modal-close" onClick={() => setShowCreditsModal(false)}>
                ✕
              </button>
            </div>
            <div className="modal-body">
              <div style={{ marginBottom: 'var(--spacing-4)', padding: 'var(--spacing-3)', background: 'var(--color-gray-50)', borderRadius: 'var(--radius-lg)' }}>
                <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-600)' }}>
                  Current Credits
                </div>
                <div style={{ fontSize: 'var(--text-2xl)', fontWeight: 'var(--font-bold)', color: 'var(--color-gray-900)' }}>
                  {selectedUser.credits}
                </div>
              </div>
              <div className="input-group">
                <label className="input-label">Amount (use negative to subtract)</label>
                <input
                  type="number"
                  className="input"
                  value={creditsForm.amount}
                  onChange={(e) => setCreditsForm({ ...creditsForm, amount: parseInt(e.target.value) || 0 })}
                  placeholder="e.g., 100 or -50"
                />
              </div>
              <div className="input-group">
                <label className="input-label">Reason</label>
                <textarea
                  className="input"
                  rows={3}
                  value={creditsForm.reason}
                  onChange={(e) => setCreditsForm({ ...creditsForm, reason: e.target.value })}
                  placeholder="Reason for adjustment..."
                />
              </div>
              <div style={{ padding: 'var(--spacing-3)', background: 'var(--color-gray-50)', borderRadius: 'var(--radius-lg)' }}>
                <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-600)' }}>
                  New Balance
                </div>
                <div style={{ fontSize: 'var(--text-xl)', fontWeight: 'var(--font-bold)', color: creditsForm.amount >= 0 ? 'var(--color-success)' : 'var(--color-error)' }}>
                  {selectedUser.credits + creditsForm.amount}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setShowCreditsModal(false)}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={handleSaveCredits}>
                Apply Adjustment
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUsers;
